import React, { memo, useMemo, useState } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import type { TimetableGrid } from '../../../services/timetableService';
import { DAY_SHORT, orderedDays, periodsOn } from '../timetableUtils';
import type { DropInfo } from './boardModel';
import type { GridItem } from '../WeekGrid';

export interface MasterRow {
  key: string;
  label: string;
  items: GridItem[];
}

interface MasterGridProps {
  grid: TimetableGrid;
  rows: MasterRow[];
  /** While dragging: drop results for the dragged card's own row, keyed `day:period`. */
  drops?: { rowKey: string; map: Map<string, DropInfo> } | null;
  onDrop?: (day: number, period: number, occupantId?: string) => void;
  onRowClick?: (key: string) => void;
}

const CELL = 58;
const LABEL = 104;

/**
 * The whole school at once: one row per class, the week across. Built for
 * scanning and quick moves inside a row; a class's own week has the detail.
 */
const MasterGrid = memo<MasterGridProps>(({ grid, rows, drops, onDrop, onRowClick }) => {
  const theme = useTheme();
  const [hover, setHover] = useState<string | null>(null);
  const days = useMemo(() => orderedDays(grid), [grid]);
  const columns = useMemo(
    () => days.flatMap((day) => Array.from({ length: periodsOn(grid, day) }, (_, i) => ({ day, period: i + 1, first: i === 0 }))),
    [days, grid],
  );
  const template = `${LABEL}px repeat(${columns.length}, ${CELL}px)`;
  const dayEdge = `2px solid ${theme.palette.divider}`;

  const tone = (info?: DropInfo) => {
    if (!info) return {};
    const { success, warning, primary, error } = theme.palette;
    switch (info.kind) {
      case 'ok': return { bgcolor: alpha(success.main, 0.12) };
      case 'prefer': return { bgcolor: alpha(warning.main, 0.14) };
      case 'swap': return { outline: `2px dashed ${alpha(primary.main, 0.75)}`, outlineOffset: -2 };
      case 'blocked': return { bgcolor: alpha(error.main, 0.06) };
      default: return {};
    }
  };

  return (
    <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 260px)', borderRadius: '12px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: template, width: 'max-content' }}>
        {/* Day header */}
        <Box sx={{ position: 'sticky', left: 0, top: 0, zIndex: 3, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }} />
        {days.map((day) => (
          <Box
            key={day}
            sx={{
              gridColumn: `span ${periodsOn(grid, day)}`, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper',
              borderBottom: '1px solid', borderLeft: dayEdge, borderColor: 'divider', px: 1, py: 0.75,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{DAY_SHORT[day]}</Typography>
          </Box>
        ))}
        {/* Period numbers */}
        <Box sx={{ position: 'sticky', left: 0, top: 30, zIndex: 3, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }} />
        {columns.map((c) => (
          <Box
            key={`${c.day}:${c.period}`}
            sx={{
              position: 'sticky', top: 30, zIndex: 2, bgcolor: 'background.paper', textAlign: 'center',
              borderBottom: '1px solid', borderLeft: c.first ? dayEdge : '1px solid', borderColor: 'divider', py: 0.25,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.66rem' }}>{c.period}</Typography>
          </Box>
        ))}

        {rows.map((row) => {
          const starts = new Map(row.items.map((i) => [`${i.day}:${i.period}`, i]));
          const covered = new Set(row.items.filter((i) => i.span === 2).map((i) => `${i.day}:${i.period + 1}`));
          const rowDrops = drops?.rowKey === row.key ? drops.map : null;
          return (
            <React.Fragment key={row.key}>
              <Box
                onClick={onRowClick ? () => onRowClick(row.key) : undefined}
                sx={{
                  position: 'sticky', left: 0, zIndex: 1, bgcolor: rowDrops ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                  borderBottom: '1px solid', borderColor: 'divider', px: 1.25, display: 'flex', alignItems: 'center',
                  cursor: onRowClick ? 'pointer' : 'default', '&:hover': onRowClick ? { color: 'primary.main' } : undefined,
                }}
              >
                <Typography variant="caption" noWrap sx={{ fontWeight: 700, fontSize: '0.74rem' }}>{row.label}</Typography>
              </Box>
              {columns.map((c) => {
                const key = `${c.day}:${c.period}`;
                if (covered.has(key)) return null;
                const item = starts.get(key);
                const info = rowDrops?.get(key);
                const canDrop = info && (info.kind === 'ok' || info.kind === 'prefer' || info.kind === 'swap');
                return (
                  <Box
                    key={key}
                    title={hover === key && info?.reason ? info.reason : undefined}
                    onDragOver={rowDrops ? (e) => { if (canDrop) e.preventDefault(); setHover(key); } : undefined}
                    onDragLeave={rowDrops ? () => setHover((h) => (h === key ? null : h)) : undefined}
                    onDrop={rowDrops ? (e) => { e.preventDefault(); setHover(null); if (canDrop) onDrop?.(c.day, c.period, item?.id); } : undefined}
                    sx={{
                      gridColumn: item?.span === 2 ? 'span 2' : undefined,
                      height: 44, p: '3px', borderBottom: '1px solid', borderLeft: c.first ? dayEdge : '1px solid', borderColor: 'divider',
                      ...tone(info),
                    }}
                  >
                    {item?.node}
                  </Box>
                );
              })}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
});

MasterGrid.displayName = 'MasterGrid';

export default MasterGrid;
